import { nanoid } from "nanoid";

export type EntityType = "artwork" | "artist" | "selection" | "constellation" | "CTA";

export interface CardAttributionContext {
  exposure_id: string;
  entity_type: EntityType;
  entity_id: string | number;
  entity_slug?: string;
  list_context?: string;
  section?: string;
  position?: number;
  filters?: Record<string, any>;
  sort?: string;
}

export interface UtmParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface PageViewedPayload {
  page_name: string;
  route_template?: string;
  path: string;
  referrer?: string;
  landing_page?: string;
  view_instance_id: string;
  entry_exposure_id?: string;
}

export interface SectionViewedPayload {
  page_name: string;
  section: string;
  position?: number;
  view_instance_id: string;
}

export interface CardImpressionPayload {
  entity_type: EntityType;
  entity_id: string | number;
  entity_slug?: string;
  list_context: string;
  section?: string;
  position?: number;
  exposure_id: string;
  filters?: Record<string, any>;
  sort?: string;
}

export interface CardClickedPayload extends CardImpressionPayload {
  destination_path: string;
  click_target?: string;
}

export interface ArtworkDetailViewedPayload {
  artwork_id: string | number;
  artwork_slug?: string;
  artist_id?: string | number;
  price?: number;
  currency?: string;
  availability?: string;
  entry_exposure_id?: string;
  referrer_page?: string;
}

export interface CatalogueSearchPayload {
  search_term_normalized: string;
  results_count: number;
  list_context: string;
}

export interface FilterAppliedPayload {
  filter_name: string;
  filter_value: any;
  results_count?: number;
  list_context: string;
}

export interface SortChangedPayload {
  sort: string;
  results_count?: number;
  list_context: string;
}

export interface ArtworkSavedPayload {
  artwork_id: string | number;
  artwork_slug?: string;
  list_context?: string;
  exposure_id?: string;
  detail_or_card: "detail" | "card";
  action?: "saved" | "unsaved";
}

export interface SelectionItemAddedPayload {
  artwork_id: string | number;
  selection_id?: string | number;
  source_context?: string;
  exposure_id?: string;
}

export interface ArtworkSharedPayload {
  artwork_id: string | number;
  share_channel?: string;
  source_context?: string;
}

export interface PurchaseCtaClickedPayload {
  artwork_id: string | number;
  cta_label: string;
  purchase_path_type: "buy_now" | "add_to_cart" | "enquire" | "reserve" | "custom";
  source_context?: string;
  exposure_id?: string;
}

export interface ExternalPurchaseHandoffPayload {
  artwork_id?: string | number;
  destination_type: "whatsapp" | "external_form" | "checkout";
  destination_domain?: string;
  cta_label: string;
  source_context?: string;
  handoff_id: string;
  exposure_id?: string;
}

export interface PurchaseEnquiryStartedPayload {
  artwork_id?: string | number;
  form_name: string;
  source_context?: string;
}

export interface PurchaseEnquirySubmittedPayload {
  artwork_id?: string | number;
  form_name: string;
  source_context?: string;
}

export interface FormAbandonedPayload {
  form_name: string;
  artwork_id?: string | number;
  last_step?: string | number;
  fields_completed_count?: number;
}

export interface NoResultsViewedPayload {
  search_term_normalized?: string;
  filters?: Record<string, any>;
  sort?: string;
  list_context: string;
}

export interface ErrorSeenPayload {
  error_area: string;
  error_code?: string | number;
  route_template?: string;
}

export interface CatalogueLoadDepthPayload {
  list_context: string;
  depth_page: number;
  items_count: number;
}

// Global in-memory / session state
const currentViewInstanceId = ref<string>(nanoid(12));
const currentCardAttribution = ref<CardAttributionContext | null>(null);

const DISALLOWED_PII_KEYS = new Set([
  "name",
  "email",
  "phone",
  "phoneNumber",
  "phone_number",
  "address",
  "street",
  "pin",
  "pincode",
  "pan",
  "password",
  "card",
  "cvv",
  "message",
  "notes",
  "comments",
]);

function sanitizePayload(payload: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (DISALLOWED_PII_KEYS.has(key.toLowerCase())) {
      continue;
    }
    if (value !== undefined && value !== null) {
      if (typeof value === "object" && !Array.isArray(value)) {
        sanitized[key] = sanitizePayload(value);
      } else {
        sanitized[key] = value;
      }
    }
  }
  return sanitized;
}

export function useAnalytics() {
  const nuxtApp = useNuxtApp();

  const getPostHog = () => {
    if (import.meta.server) return null;
    try {
      const ph = typeof (nuxtApp as any)?.$posthog === "function"
        ? (nuxtApp as any).$posthog()
        : (nuxtApp as any)?.$posthog || (window as any)?.posthog;
      return ph && typeof ph.capture === "function" ? ph : null;
    } catch {
      return (window as any)?.posthog || null;
    }
  };

  const identifyUser = (distinctId: string, properties: Record<string, any> = {}) => {
    if (import.meta.server || !distinctId) return;
    try {
      const ph = getPostHog();
      if (ph && typeof ph.identify === "function") {
        ph.identify(distinctId, properties);
      }
    } catch (err) {
      console.warn("[useAnalytics] Error identifying user:", err);
    }
  };

  const resetUser = () => {
    if (import.meta.server) return;
    try {
      const ph = getPostHog();
      if (ph && typeof ph.reset === "function") {
        ph.reset();
      }
    } catch (err) {
      console.warn("[useAnalytics] Error resetting user:", err);
    }
  };

  const capture = (eventName: string, properties: Record<string, any> = {}) => {
    try {
      const sanitized = sanitizePayload(properties);
      const ph = getPostHog();
      if (ph) {
        ph.capture(eventName, sanitized);
      }
    } catch (err) {
      console.warn(`[useAnalytics] Error capturing ${eventName}:`, err);
    }
  };

  const generateExposureId = () => `exp_${nanoid(12)}`;
  const generateHandoffId = () => `hnd_${nanoid(10)}`;
  const generateViewInstanceId = () => `view_${nanoid(12)}`;

  const setViewInstanceId = (id?: string) => {
    currentViewInstanceId.value = id || generateViewInstanceId();
    return currentViewInstanceId.value;
  };

  const getViewInstanceId = () => currentViewInstanceId.value;

  const setCardAttribution = (context: CardAttributionContext) => {
    currentCardAttribution.value = context;
    if (import.meta.client) {
      try {
        sessionStorage.setItem("qam_last_exposure", JSON.stringify(context));
      } catch (_) {}
    }
  };

  const getCardAttribution = (): CardAttributionContext | null => {
    if (currentCardAttribution.value) {
      return currentCardAttribution.value;
    }
    if (import.meta.client) {
      try {
        const stored = sessionStorage.getItem("qam_last_exposure");
        if (stored) {
          currentCardAttribution.value = JSON.parse(stored);
          return currentCardAttribution.value;
        }
      } catch (_) {}
    }
    return null;
  };

  // Specific event tracking methods following the measurement plan
  const trackPageView = (payload: PageViewedPayload) => {
    capture("page_viewed", payload);
  };

  const trackSectionView = (payload: Partial<SectionViewedPayload> & { section: string; [key: string]: any }) => {
    capture("section_viewed", {
      page_name: payload.page_name || (typeof window !== "undefined" ? window.location.pathname : ""),
      view_instance_id: payload.view_instance_id || getViewInstanceId(),
      ...payload,
    });
  };

  const trackArtworkShareClicked = (payload: any) => {
    capture("artwork_shared", payload);
  };

  const trackCardImpression = (payload: CardImpressionPayload) => {
    capture("card_impression", payload);
  };

  const trackCardClick = (payload: CardClickedPayload) => {
    setCardAttribution({
      exposure_id: payload.exposure_id,
      entity_type: payload.entity_type,
      entity_id: payload.entity_id,
      entity_slug: payload.entity_slug,
      list_context: payload.list_context,
      section: payload.section,
      position: payload.position,
      filters: payload.filters,
      sort: payload.sort,
    });
    capture("card_clicked", payload);
  };

  const trackArtworkDetailViewed = (payload: ArtworkDetailViewedPayload) => {
    const attr = getCardAttribution();
    const entryExposureId = payload.entry_exposure_id || attr?.exposure_id;
    capture("artwork_detail_viewed", {
      ...payload,
      entry_exposure_id: entryExposureId,
    });
  };

  const trackCatalogueSearchSubmitted = (payload: CatalogueSearchPayload) => {
    capture("catalogue_search_submitted", payload);
  };

  const trackFilterApplied = (payload: FilterAppliedPayload) => {
    capture("filter_applied", payload);
  };

  const trackSortChanged = (payload: SortChangedPayload) => {
    capture("sort_changed", payload);
  };

  const trackArtworkSaved = (payload: ArtworkSavedPayload) => {
    const attr = getCardAttribution();
    capture("artwork_saved", {
      exposure_id: payload.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackSelectionItemAdded = (payload: SelectionItemAddedPayload) => {
    const attr = getCardAttribution();
    capture("selection_item_added", {
      exposure_id: payload.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackArtworkShared = (payload: ArtworkSharedPayload) => {
    capture("artwork_shared", payload);
  };

  const trackPurchaseCtaClicked = (payload: PurchaseCtaClickedPayload) => {
    const attr = getCardAttribution();
    capture("purchase_cta_clicked", {
      exposure_id: payload.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackExternalPurchaseHandoff = (payload: ExternalPurchaseHandoffPayload) => {
    const attr = getCardAttribution();
    capture("external_purchase_handoff_clicked", {
      exposure_id: payload.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackPurchaseHandoffStarted = (payload: any) => {
    const attr = getCardAttribution();
    capture("purchase_handoff_started", {
      exposure_id: payload?.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackPurchaseHandoffFailed = (payload: any) => {
    const attr = getCardAttribution();
    capture("purchase_handoff_failed", {
      exposure_id: payload?.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackPurchaseHandoffAbandoned = (payload: any) => {
    const attr = getCardAttribution();
    capture("purchase_handoff_abandoned", {
      exposure_id: payload?.exposure_id || attr?.exposure_id,
      ...payload,
    });
  };

  const trackMovementFormStarted = (payload: any) => {
    capture("movement_form_started", payload);
  };

  const trackMovementFormSubmitted = (payload: any) => {
    capture("movement_form_submitted", payload);
  };

  const trackSelectionCreated = (payload: any) => {
    capture("selection_created", payload);
  };

  const trackPurchaseEnquiryStarted = (payload: PurchaseEnquiryStartedPayload) => {
    capture("purchase_enquiry_started", payload);
  };

  const trackPurchaseEnquirySubmitted = (payload: PurchaseEnquirySubmittedPayload) => {
    capture("purchase_enquiry_submitted", payload);
  };

  const trackFormAbandoned = (payload: FormAbandonedPayload) => {
    capture("form_abandoned", payload);
  };

  const trackNoResultsViewed = (payload: NoResultsViewedPayload) => {
    capture("no_results_viewed", payload);
  };

  const trackErrorSeen = (payload: ErrorSeenPayload) => {
    capture("error_seen", payload);
  };

  const trackCatalogueLoadDepth = (payload: CatalogueLoadDepthPayload) => {
    capture("catalogue_load_depth", payload);
  };

  return {
    capture,
    generateExposureId,
    generateHandoffId,
    generateViewInstanceId,
    setViewInstanceId,
    getViewInstanceId,
    setCardAttribution,
    getCardAttribution,
    trackPageView,
    trackSectionView,
    trackSectionViewed: trackSectionView,
    trackCardImpression,
    trackCardClick,
    trackArtworkDetailViewed,
    trackCatalogueSearchSubmitted,
    trackFilterApplied,
    trackCatalogueFilterApplied: trackFilterApplied,
    trackSortChanged,
    trackArtworkSaved,
    trackSelectionItemAdded,
    trackSelectionCreated,
    trackArtworkShared,
    trackArtworkShareClicked,
    trackPurchaseCtaClicked,
    trackExternalPurchaseHandoff,
    trackPurchaseHandoffStarted,
    trackPurchaseHandoffFailed,
    trackPurchaseHandoffAbandoned,
    trackMovementFormStarted,
    trackMovementFormSubmitted,
    trackPurchaseEnquiryStarted,
    trackPurchaseEnquirySubmitted,
    trackFormAbandoned,
    trackNoResultsViewed,
    trackErrorSeen,
    trackCatalogueLoadDepth,
    identifyUser,
    resetUser,
  };
}
